// @flow

import React from 'react';
import { connect } from 'react-redux';
import { type HOC } from 'recompose';
import styled from '@emotion/styled';
import { padding } from 'polished';

import bonusesSelector from 'app/selectors/bonusesSelector';
import ChallengeInfo from './ChallengeInfo';
import ChallengeOverview from './ChallengeOverview';
import ChallengeSocialShareButtons from './ChallengeSocialShareButtons';
import Responsive from '../responsive/Responsive';
import PoweredBy from '../icons/PoweredBy';
import EntryPill from './EntryPill';

type Props = {
  bonuses: Array<*>,
  countries: Array<*>,
  disableTabs?: Array<string>,
  entryCap: number,
  entryCount: number,
  games: Array<*>,
  id: string,
  isChallengeBuilder?: boolean,
  isTargetChallenge: boolean,
  isRacing?: boolean,
  isUnlimited: boolean,
  labels: Array<*>,
  maxEntriesPerPerson: number,
  name: string,
  payoutLongDescription?: string,
  prizes?: Array<*>,
  scheme: 'light' | 'dark',
  scoring: Array<*>,
  showChallengeOverview: boolean,
  statusId: number,
  userEntryCount: number
};

const Wrapper = styled.div(({ theme }) => ({
  backgroundColor: theme.color.basis2,
  flexGrow: 1
}));

const Extra = styled.div(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'column',
  ...padding(null, theme.spacing.small, theme.spacing.small),
  [theme.mediaQuery.mobileOnly]: {
    '& > *': {
      margin: theme.spacing.tiny
    }
  },
  [theme.mediaQuery.tabletPortraitAndUp]: {
    position: 'relative',
    alignItems: 'flex-end',
    flexDirection: 'row',
    ...padding(null, theme.spacing.medium)
  },
  [theme.mediaQuery.desktopLargeOnly]: {
    marginTop: -10
  }
}));

const ChallengeHead: React.FC<Props> = ({
  bonuses,
  countries,
  disableTabs,
  entryCap,
  entryCount,
  games,
  id,
  isChallengeBuilder,
  isTargetChallenge,
  isRacing,
  isUnlimited,
  labels,
  maxEntriesPerPerson,
  name,
  payoutLongDescription,
  prizes,
  scheme,
  scoring,
  showChallengeOverview,
  statusId,
  userEntryCount
}: Props) => {
  const [activeTabItem, setActiveTabItem] = React.useState('games');

  return (
    <Responsive mobile={showChallengeOverview} desktop>
      <Wrapper>
        <ChallengeOverview
          title={name}
          hideSecondaryInfo={isChallengeBuilder}
          isTargetChallenge={isTargetChallenge}
        />
        {!isChallengeBuilder && (
          <Extra>
            {userEntryCount > 0 && (
              <EntryPill maxEntriesPerPerson={maxEntriesPerPerson} count={userEntryCount} />
            )}
            <ChallengeSocialShareButtons statusId={statusId} />
            <PoweredBy />
          </Extra>
        )}
      </Wrapper>
      <ChallengeInfo
        activeTabItem={activeTabItem}
        bonuses={bonuses}
        challengeId={id}
        countries={countries}
        disableTabs={disableTabs}
        entryCap={entryCap}
        entryCount={entryCount}
        games={games}
        isRacing={isRacing}
        isUnlimited={isUnlimited}
        labels={labels}
        maxEntriesPerPerson={maxEntriesPerPerson}
        onChange={tabItem => setActiveTabItem(tabItem)}
        onClose={() => setActiveTabItem(null)}
        payoutMessage={payoutLongDescription}
        prizes={prizes}
        scheme={scheme}
        scoring={scoring}
        statusId={statusId}
      />
    </Responsive>
  );
};

ChallengeHead.defaultProps = {
  disableTabs: [],
  isChallengeBuilder: false,
  isRacing: false,
  payoutLongDescription: undefined,
  prizes: []
};

const connector: HOC<GlobalState, Props> = connect(
  state => ({
    bonuses: bonusesSelector(state),
    countries: state.challenge.countries,
    entryCap: state.challenge.entryCap,
    entryCount: state.challenge.entryCount,
    games: state.challenge.games,
    id: state.challenge.id,
    isTargetChallenge: state.challenge.isTargetChallenge,
    isRacing: state.challenge.isRacing,
    isUnlimited: state.challenge.isUnlimited,
    labels: state.challenge.labels,
    maxEntriesPerPerson: state.challenge.maxEntriesPerPerson,
    name: state.challenge.name,
    payoutLongDescription: state.challenge.payoutLongDescription,
    statusId: state.challenge.statusId,
    scheme: state.settings.darkMode ? 'dark' : 'light',
    scoring: state.challenge.scoring,
    userEntryCount: state.challenge.userEntryIds.length
  }),
  null
);

export default connector(ChallengeHead);
